import React, { useState } from 'react';
import {
  Activity,
  TrendingUp,
  AlertTriangle,
  History,
  Zap,
  BarChart3,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

interface DashboardSidebarProps {
  site: string;
  activeCategory: string;
  onSelectCategory: (category: string) => void;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  site,
  activeCategory,
  onSelectCategory,
}) => {
  // Collapsed by default as per user request, with localStorage persistence
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('dashboard_sidebar_collapsed');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('dashboard_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const menuItems = [
    {
      key: 'live-2',
      label: 'Live',
      icon: Activity,
      description: 'Plant & Central Inverter Live'
    },
    {
      key: 'performance-analytics-2',
      label: 'Performance Analytics',
      icon: TrendingUp,
      description: 'Energy vs GII & Plant Performance'
    },
    {
      key: 'forced-outages',
      label: 'Forced Outages',
      icon: AlertTriangle,
      description: 'Plant Breakdown Analysis'
    },
    {
      key: 'historian',
      label: 'Historian',
      icon: History,
      description: 'Historical + YoY / MoM'
    },
    {
      key: 'virtual-power-plant',
      label: 'Virtual Power Plant',
      icon: Zap,
      description: 'Coming Soon'
    }
  ];

  return (
    <div
      className="dashboard-sidebar-container"
      style={{
        width: isCollapsed ? '64px' : '250px',
        minWidth: isCollapsed ? '64px' : '250px',
        height: '100%',
        backgroundColor: '#FFFFFF',
        borderRight: '1.5px solid #0084CF',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'IBM Plex Sans', sans-serif",
        boxShadow: '2px 0 6px rgba(0, 0, 0, 0.04)',
        flexShrink: 0,
        position: 'relative',
        zIndex: 2,
        transition: 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar Header */}
      <div
        style={{
          height: '68px',
          minHeight: '68px',
          padding: '0 14px',
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#F8FAFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'flex-start' : 'space-between',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {/* Static Header Icon - Fixed position, never clipped */}
          <div
            onClick={isCollapsed ? toggleCollapse : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              backgroundColor: '#0084CF',
              borderRadius: '8px',
              flexShrink: 0,
              boxShadow: '0 2px 5px rgba(0, 132, 207, 0.3)',
              cursor: isCollapsed ? 'pointer' : 'default',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (isCollapsed) {
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={isCollapsed ? 'Click to expand sidebar' : undefined}
          >
            <BarChart3 size={18} color="#FFFFFF" />
          </div>

          {/* Header Text - Expands/collapses smoothly without shifting the icon */}
          <div
            style={{
              marginLeft: isCollapsed ? 0 : '10px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              opacity: isCollapsed ? 0 : 1,
              maxWidth: isCollapsed ? 0 : '130px',
              transform: isCollapsed ? 'translateX(-8px)' : 'translateX(0)',
              pointerEvents: isCollapsed ? 'none' : 'auto',
              transition: 'max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease, transform 0.22s ease, margin-left 0.22s ease',
            }}
          >
            <div
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#0F172A',
                letterSpacing: '-0.2px',
              }}
            >
              Dashboard
            </div>
          </div>
        </div>

        {/* Header Toggle button - Only rendered when expanded */}
        {!isCollapsed && (
          <button
            onClick={toggleCollapse}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              cursor: 'pointer',
              color: '#64748B',
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F2F8FC';
              e.currentTarget.style.color = '#0084CF';
              e.currentTarget.style.borderColor = '#C2DFF1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.color = '#64748B';
              e.currentTarget.style.borderColor = '#E2E8F0';
            }}
            title="Collapse sidebar"
          >
            <ChevronsLeft size={16} />
          </button>
        )}
      </div>

      {/* Menu Items List */}
      <div
        style={{
          flex: 1,
          padding: '12px 8px', // Fixed padding, identical for collapsed and expanded
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = activeCategory === item.key;

          return (
            <div
              key={item.key}
              onClick={() => onSelectCategory(item.key)}
              title={isCollapsed ? `${item.label} (${item.description})` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start', // Anchored to left, never toggles to center
                padding: '7px 4px',
                borderRadius: '8px',
                backgroundColor: active ? '#E3F0F9' : 'transparent',
                color: active ? '#0084CF' : '#475569',
                cursor: 'pointer',
                transition: 'background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease',
                borderLeft: active ? '3.5px solid #0084CF' : '3.5px solid transparent',
                position: 'relative',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = '#F1F5F9';
                  e.currentTarget.style.color = '#0F172A';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#475569';
                }
              }}
            >
              {/* Static Icon Container - Fixed width and position, center at exactly 32px */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '34px',
                  height: '34px',
                  flexShrink: 0,
                }}
              >
                <Icon
                  size={19}
                  color={active ? '#0084CF' : '#64748B'}
                  strokeWidth={active ? 2.3 : 1.8}
                />
              </div>

              {/* Text Container - Expands/collapses smoothly while icon remains completely static */}
              <div
                style={{
                  marginLeft: '10px',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  opacity: isCollapsed ? 0 : 1,
                  maxWidth: isCollapsed ? 0 : '185px',
                  transform: isCollapsed ? 'translateX(-8px)' : 'translateX(0)',
                  pointerEvents: isCollapsed ? 'none' : 'auto',
                  transition: 'max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease, transform 0.22s ease',
                }}
              >
                <div
                  style={{
                    fontSize: '13.5px',
                    fontWeight: active ? 600 : 500,
                    color: active ? '#0084CF' : '#1E293B',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.2',
                  }}
                >
                  {item.label}
                </div>
                {item.description && (
                  <div
                    style={{
                      fontSize: '10.5px',
                      color: active ? '#0084CF' : '#64748B',
                      marginTop: '2px',
                      fontWeight: active ? 500 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.1',
                    }}
                  >
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Toggle Button */}
      <div
        style={{
          borderTop: '1px solid #E5E7EB',
          padding: '10px 14px',
          backgroundColor: '#F8FAFC',
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <button
          onClick={toggleCollapse}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isCollapsed ? 0 : '6px',
            padding: isCollapsed ? '6px' : '5px 10px',
            border: '1px solid #CBD5E1',
            borderRadius: '6px',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer',
            color: '#475569',
            fontSize: '11px',
            fontWeight: 500,
            transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
            width: isCollapsed ? '36px' : 'auto',
            minWidth: isCollapsed ? '36px' : 'auto',
            height: '30px',
            justifyContent: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F2F8FC';
            e.currentTarget.style.color = '#0084CF';
            e.currentTarget.style.borderColor = '#8AC4E6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
            e.currentTarget.style.color = '#475569';
            e.currentTarget.style.borderColor = '#CBD5E1';
          }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronsRight size={15} color="#0084CF" />
          ) : (
            <>
              <ChevronsLeft size={15} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DashboardSidebar;
